import type { 
  RealNotification, 
  Notification, 
  NotificationCategory, 
  NotificationPriority,
  NotificationMsgPayload
} from '@/types';

/**
 * Parses Python-style stringified dictionaries into JS objects.
 * Python uses single quotes and 'None', 'True', 'False'.
 */
export const parseNotificationMsg = (msgStr: string): NotificationMsgPayload => {
  try {
    // Basic conversion to JSON-friendly format
    // Python dictionaries use single quotes and capitalized literals (None, True, False)
    
    let processed = msgStr
      .replace(/:\s*None/g, ': null')
      .replace(/:\s*True/g, ': true')
      .replace(/:\s*False/g, ': false');

    // 1. Try to handle the keys which are almost always 'key': 
    processed = processed.replace(/'([^']+)':/g, '"$1":');

    // 2. Try to handle string values. This is trickier because of nested quotes.
    // If it's already "value", leave it. If it's 'value', convert to "value" and escape internal double quotes.
    // We look for values followed by , or }
    processed = processed.replace(/:\s*'([^']*)'(\s*[,}])/g, (_match, p1, p2) => {
       const escaped = p1.replace(/"/g, '\\"');
       return `: "${escaped}"${p2}`;
    });
    
    return JSON.parse(processed);
  } catch (error) {
    console.warn('Failed to parse notification message:', msgStr, error);
    
    // Improved Fallback: Extract 'text' field using multiple patterns
    // Pattern 1: 'text': "..." (double quotes)
    const matchDouble = msgStr.match(/'text':\s*"([^"]*)"/);
    if (matchDouble) return { text: matchDouble[1], notification_type: 'Unknown' };

    // Pattern 2: 'text': '...' (single quotes)
    const matchSingle = msgStr.match(/'text':\s*'([^']*)'/);
    if (matchSingle) return { text: matchSingle[1], notification_type: 'Unknown' };

    // Pattern 3: "text": "..." (already JSON-ish but maybe failed elsewhere)
    const matchJson = msgStr.match(/"text":\s*"([^"]*)"/);
    if (matchJson) return { text: matchJson[1], notification_type: 'Unknown' };

    return { text: msgStr, notification_type: 'Unknown' };
  }
};

export const transformRealToUI = (real: RealNotification): Notification => {
  const payload = parseNotificationMsg(real.notification.msg);
  
  // Map notification_type to category
  let category: NotificationCategory = 'system';
  if (real.notification.notification_type.toLowerCase().includes('project')) category = 'project';
  if (real.notification.notification_type.toLowerCase().includes('course')) category = 'course';
  if (real.notification.notification_type.toLowerCase().includes('organization')) category = 'organization';

  // Map priority (default to medium)
  const priority: NotificationPriority = 'medium';

  return {
    id: real.id.toString(),
    title: real.notification.notification_type,
    message: payload.text || '',
    timestamp: real.notification.created_date_ad,
    status: real.is_read ? 'read' : 'unread',
    priority,
    category,
    metadata: payload
  };
};
